package com.videoanalyzer.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*

/**
 * Loading spinner component.
 *
 * ANDROID EQUIVALENT: CircularProgressIndicator in Jetpack Compose
 */
@Composable
fun LoadingSpinner(message: String = "Loading...") {
    Div({
        classes("fade-in")
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(40.px)
            gap(16.px)
        }
    }) {
        // Simple spinner - no glow
        Div({
            classes("spinner", "spinner-large")
            style {
                property("border-color", "#3F3F46")
                property("border-top-color", "#0EA5E9")
            }
        })

        // Loading message
        Div({
            style {
                textAlign("center")
            }
        }) {
            Span({
                style {
                    color(Color("#FAFAFA"))
                    fontSize(14.px)
                    fontWeight(500)
                    display(DisplayStyle.Block)
                    marginBottom(6.px)
                }
            }) {
                Text(message)
            }

            // Loading dots
            Div({ classes("loading-dots") }) {
                Span({})
                Span({})
                Span({})
            }
        }
    }
}

/**
 * Full-page loading state with background.
 */
@Composable
fun FullPageLoading(message: String = "Loading...") {
    Div({
        classes("fade-in")
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(100.vh)
            backgroundColor(Color("#09090B"))
        }
    }) {
        // Logo
        Div({
            style {
                marginBottom(24.px)
            }
        }) {
            Logo(size = LogoSize.LARGE, animate = false)
        }

        // Spinner
        Div({
            classes("spinner", "spinner-large")
            style {
                marginBottom(16.px)
                property("border-color", "#3F3F46")
                property("border-top-color", "#0EA5E9")
            }
        })

        // Message
        Span({
            style {
                color(Color("#A1A1AA"))
                fontSize(14.px)
            }
        }) {
            Text(message)
        }
    }
}

/**
 * Inline loading indicator (smaller).
 */
@Composable
fun InlineLoading() {
    Div({
        classes("spinner")
        style {
            width(16.px)
            height(16.px)
            property("border-width", "2px")
            property("border-color", "rgba(255, 255, 255, 0.2)")
            property("border-top-color", "white")
        }
    })
}

/**
 * Skeleton loading placeholder.
 */
@Composable
fun SkeletonLine(width: CSSNumeric = 100.percent, height: CSSNumeric = 14.px) {
    Div({
        style {
            this.width(width)
            this.height(height)
            borderRadius(4.px)
            backgroundColor(Color("#27272A"))
        }
    })
}

/**
 * Skeleton loading card.
 */
@Composable
fun SkeletonCard() {
    Div({
        classes("card")
        style {
            padding(20.px)
        }
    }) {
        SkeletonLine(width = 60.percent, height = 20.px)
        Div({ style { height(10.px) } })
        SkeletonLine(width = 100.percent)
        Div({ style { height(6.px) } })
        SkeletonLine(width = 80.percent)
        Div({ style { height(6.px) } })
        SkeletonLine(width = 40.percent)
    }
}
